import {
  WSEmptyState,
  WSFilters,
  WSInfiniteScroll,
  WSList,
  WSTableColumnSortDirection
} from "@wingspanhq/fe-component-library";
import { useCallback, useMemo } from "react";
import { useHistory } from "react-router-dom";
import { useUserId } from "../../../../query/hooks/helpers";
import { useQueryWorkLogs } from "../../../../query/workLog/queries/useQueryWorkLogs";
import { useQueryWorkLogsSummary } from "../../../../query/workLog/queries/useQueryWorkLogsSummary";
import { WSQueries } from "../../../../query/WSQuery";
import { useUrlQuery } from "../../../../shared/hooks/useUrlQuery";
import { useInvoicesMap } from "../../hooks/useInvoicesMap";
import { usePayersMap } from "../../hooks/usePayersMap";
import { getPathWorkLogPayeeDetails } from "../../paths";
import { useBuildConfig } from "./filters/config";
import { createListRequest } from "./filters/request";
import { Filter, SortField } from "./filters/types";
import { TableWorkLogsPayeeView } from "./TableWorkLogsPayeeView";

type Props = {
  status?: Filter["status"];
};

export const FilterableTableWorkLogsPayeeView: React.FC<Props> = props => {
  const userId = useUserId();
  const history = useHistory();

  const { urlQuery, setUrlQuery } = useUrlQuery<Filter, SortField>();

  const request = useMemo(
    () =>
      createListRequest(
        userId,
        {
          status: props.status,
          ...urlQuery.filter
        },
        urlQuery.sort
      ),
    [userId, props.status, urlQuery.filter, urlQuery.sort]
  );

  const filtersConfig = useBuildConfig();

  const queryWorkLogs = useQueryWorkLogs(request);
  const queryWorkLogsSummary = useQueryWorkLogsSummary(request);

  const payersMap = usePayersMap(queryWorkLogs.data);
  const { invoicesMap } = useInvoicesMap(queryWorkLogs.data);

  const hasFilters = useMemo(
    () => Object.keys(urlQuery.filter || {}).length > 0,
    [urlQuery.filter]
  );

  const sortByUpdatedAt = useCallback(
    (newDirection: WSTableColumnSortDirection) => {
      setUrlQuery(prev => ({
        ...prev,
        sort: {
          field: "updatedAt",
          direction: newDirection
        }
      }));
    },
    [setUrlQuery]
  );

  return (
    <WSList gap="2XL">
      <WSFilters<Filter>
        values={urlQuery.filter}
        onFilter={newValue => {
          setUrlQuery({ filter: newValue });
        }}
        primaryFilters={filtersConfig}
        info={{
          count: queryWorkLogsSummary.data?.listSize || 0,
          amount: queryWorkLogsSummary.data?.totalAmount
        }}
      />

      <WSQueries queries={{ queryWorkLogs }}>
        {({ queryWorkLogsData }) =>
          hasFilters && queryWorkLogsData.length === 0 ? (
            <WSEmptyState
              py="3XL"
              hasBorder
              bgColor="gray"
              type="search"
              title="No results found"
              description="Try adjusting your search or filter to find what you're looking for."
            />
          ) : queryWorkLogsData.length === 0 ? (
            props.status === "in-progress" ? (
              <WSEmptyState
                py="3XL"
                hasBorder
                bgColor="gray"
                type="tasks"
                title="No work logs yet"
                description="Create a work log to track your work. Once submitted, it will generate an invoice."
              />
            ) : (
              <WSEmptyState
                py="3XL"
                hasBorder
                bgColor="gray"
                type="tasks"
                title="No submitted work logs"
                description="Once you submit your in-progress work logs, they will appear here."
              />
            )
          ) : (
            <WSInfiniteScroll
              mt="M"
              onLoad={() => queryWorkLogs.fetchNextPage()}
              loadMore={queryWorkLogsData.length > 0}
              endOfList={!queryWorkLogs.hasNextPage}
              loading={!!queryWorkLogs.isFetchingNextPage}
            >
              <TableWorkLogsPayeeView
                workLogs={queryWorkLogsData}
                payersMap={payersMap}
                invoicesMap={invoicesMap}
                onRowClick={workLog => {
                  history.push(getPathWorkLogPayeeDetails(workLog.workLogId));
                }}
                onSortByUpdatedAt={sortByUpdatedAt}
                updatedAtDirection={urlQuery.sort?.direction}
              />
            </WSInfiniteScroll>
          )
        }
      </WSQueries>
    </WSList>
  );
};
